import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  PaginationParams,
  PaginationResponse,
} from 'src/app/Theme/shared/components/pagination/pagination.interface';
import { PaginationService } from 'src/app/Theme/shared/components/pagination/pagination.service';
import { environment } from 'src/environments/environment';
import { PublishedItemsListParams } from '../../cf-product/service/product.service';
import { TfService } from 'src/app/tf/tf-service.service';

/**
 * Customer-portal category HTTP client. A few admin modules import this service by path
 * (e.g. paginated category list); `adminCustomer` scoping must match
 * `admin/cf-admin/cf-categorie/service/cf-category-service.service.ts`.
 */
@Injectable({
  providedIn: 'root'
})
export class CfCategoryService {
  private host = environment.apiUrlHost;


  private categoryNameMap: { [code: string]: any } = {}; // Changed to any to handle multilanguage titles

  /**
   * Catalog root opened from the customer category list; used to anchor the sidebar tree
   * (children of this code) while navigating deeper with the path `/sub-category/:code`.
   */
  private catalogBrowseRootCode: string | null = null;

  setCatalogBrowseRoot(code: string | null | undefined): void {
    const c = typeof code === 'string' ? code.trim() : '';
    this.catalogBrowseRootCode = c.length > 0 ? c : null;
  }

  getCatalogBrowseRoot(): string | null {
    return this.catalogBrowseRootCode;
  }

  clearCatalogBrowseRoot(): void {
    this.catalogBrowseRootCode = null;
  }

  public setCategoryName(code: string, name: any): void { // Changed to any to handle multilanguage titles
    console.log(`setCategoryName called for code: ${code}, name:`, name);
    this.categoryNameMap[code] = name;
    console.log('Updated categoryNameMap:', this.categoryNameMap);
  }

  allocateCatalog(categoryCode: string, customerPortalId: string): Observable<any> {
    const body = {
      customerPortalId,
      categoryCode
    };
    console.log("body", body);

    return this.http.post(`${this.host}/CP/allocate-category/`, body);
  }
  deleteAllocation(categoryCode: string, customerId: string | number) {
    console.log("categoryCode", categoryCode);
    console.log("customerId", customerId);
    return this.http.delete(`${this.host}/CP/delete-category-allocation/${categoryCode}/${customerId}/`);
  }

  getAllocatedCustomers(categoryCode: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.host}/CP/allocated-customers/${categoryCode}`);
  }

  public getCategoryName(code: string): string {
    const categoryData = this.categoryNameMap[code];
    console.log(`getCategoryName called for code: ${code}`);
    console.log('categoryData:', categoryData);
    
    if (!categoryData) {
      console.log('No categoryData found, returning code');
      return code; // fallback to code if name is unknown
    }

    // If it's a string, return it directly
    if (typeof categoryData === 'string') {
      console.log('categoryData is string, returning:', categoryData);
      return categoryData;
    }

    // If it's an object with language keys, try to get the localized title
    if (typeof categoryData === 'object') {
      // Try to get title in the current language
      const storedLanguageChanged = localStorage.getItem('langchanged');
      const currentLang = storedLanguageChanged || localStorage.getItem('lang') || 'en';
      console.log('Current language:', currentLang);
      console.log('categoryData:', categoryData);
      
      // Check if categoryData itself is the title object (direct from API)
      let titleObj = categoryData;
      
      // Or if it's wrapped in a 'title' property
      if (categoryData.title) {
        titleObj = categoryData.title;
      }
      
      console.log('titleObj:', titleObj);
      
      const title = titleObj?.[currentLang] || titleObj?.['en'] || Object.values(titleObj || {})[0];
      console.log('Extracted title:', title);
      
      if (title && typeof title === 'string' && title.trim() !== '') {
        console.log('Returning title:', title);
        return title;
      }
    }

    console.log('No valid title found, returning code:', code);
    return code; // fallback to code if no valid title found
  }

  // BehaviorSubject to store and track the current active category
  private activeCategorySubject = new BehaviorSubject<any>(null);
  public activeCategory$ = this.activeCategorySubject.asObservable();

  // BehaviorSubject to store the category navigation path history
  private navigationHistorySubject = new BehaviorSubject<string[]>([]);
  public navigationHistory$ = this.navigationHistorySubject.asObservable();

  constructor(
    private http: HttpClient,
    private paginationService: PaginationService,
    private tfService: TfService
  ) {
    // Try to restore navigation history from localStorage if available
    const savedHistory = localStorage.getItem('categoryNavigationHistory');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        if (Array.isArray(parsedHistory)) {
          this.navigationHistorySubject.next(parsedHistory);
        }
      } catch (error) {
        console.error('Error parsing saved navigation history:', error);
      }
    }
  }

  /** Same as admin category service: `adminCustomer` without full `admin` sees BP-scoped catalog. */
  private scopeCatalogLikeCustomerPortal(): boolean {
    const g = this.tfService.getUserGroupsFromLocalStorage();
    return g.includes('adminCustomer') && !g.includes('admin');
  }

  /**
   * Customer portal defaults to published/BP-scoped catalog (`view_publish=true`).
   * Pass `viewPublish: false` for admin layout preview (full category tree).
   * Pass `businessPartner` to scope the tree to one BP (`business_partner` query param).
   */
  private withViewPublish(params: HttpParams, viewPublish?: boolean): HttpParams {
    if (viewPublish === false) {
      return params;
    }
    return params.set('view_publish', 'true');
  }

  private buildCategoryTreeLevelsParams(options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    viewPublish?: boolean;
    businessPartner?: string | null;
  }): HttpParams {
    const page = options?.page ?? 1;
    const pageSize = Math.min(options?.pageSize ?? 30, 100);
    let params = new HttpParams()
      .set('page', String(page))
      .set('page_size', String(pageSize));
    params = this.withViewPublish(params, options?.viewPublish);
    const bp = options?.businessPartner?.trim();
    if (bp) {
      params = params.set('business_partner', bp).set('bp', bp);
    }
    const q = options?.search?.trim();
    if (q) {
      params = params.set('search', q);
    }
    return params;
  }

  /**
   * Get catalog categories (root categories with catalog=true) - NEW METADATA API
   * Returns metadata with counts only, no products
   */
  /**
   * Get catalog metadata (categories list) with backend pagination
   * @param page - Page number (default: 1)
   * @param pageSize - Page size (default: 9)
   */
  public getCatalogMetadata(page: number = 1, pageSize: number = 9): Observable<any> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());
    params = params.set('view_publish', 'true');
    return this.http.get(`${this.host}/CF/category-metadata/`, { params });
  }

  /**
   * Get single category metadata with optional products - NEW METADATA API
   * @param categoryCode - Category code
   * @param showProducts - Whether to include paginated products (default: false)
   * @param page - Page number when showProducts=true (default: 1)
   * @param pageSize - Page size when showProducts=true (default: 20)
   * @param includeDescendantsProducts - When true, products include category + all descendants (listing mode)
   */
  public getCategoryMetadata(
    categoryCode: string,
    showProducts: boolean = false,
    page: number = 1,
    pageSize: number = 20,
    includeDescendantsProducts: boolean = false,
    viewPublish?: boolean,
  ): Observable<any> {
    let params = this.withViewPublish(new HttpParams(), viewPublish);

    if (showProducts) {
      params = params.set('show_products', 'true');
      params = params.set('page', page.toString());
      params = params.set('page_size', pageSize.toString());
      if (includeDescendantsProducts) {
        params = params.set('include_descendants_products', 'true');
      }
    }

    return this.http.get(`${this.host}/CF/category-metadata/${categoryCode}/`, { params });
  }

  /**
   * Category-scoped published list with spec/price filters (same query contract as `/CF/filtered-publish-list/`).
   * Django: `category/<path:category_code>/filtered-publish-list/` → CFCategoryFilteredPublishListAPIView.
   */
  public getCategoryFilteredPublishList(
    categoryCode: string,
    params: PublishedItemsListParams & { view_publish?: boolean } = {}
  ): Observable<PaginationResponse<any>> {
    const path = categoryCode
      .split('/')
      .filter((s) => s.length > 0)
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    const q: PaginationParams = {
      ...params,
    };
    if (params.view_publish === false) {
      delete q['view_publish'];
    } else if (this.scopeCatalogLikeCustomerPortal()) {
      q['view_publish'] = true;
    } else if (params.view_publish === true) {
      q['view_publish'] = true;
    } else {
      delete q['view_publish'];
    }
    return this.paginationService.getPaginatedData<any>(
      `${this.host}/CF/category/${path}/filtered-publish-list/`,
      q
    );
  }

  /**
   * Products in the current category tree (category + descendants) that belong to a specification set.
   * GET /CF/category/&lt;category_code&gt;/spec-set/&lt;spec_set_id&gt;/products/
   * Path segments are encoded per segment (same as category-tree-levels) so codes with special characters work.
   */
  public getProductsByCategoryAndSpecSet(
    categoryCode: string,
    specSetId: string,
    params: PaginationParams & {
      view_publish?: boolean;
      item_specs?: string;
      price_min?: number;
      price_max?: number;
    } = {}
  ): Observable<PaginationResponse<any>> {
    const path = categoryCode
      .split('/')
      .filter((s) => s.length > 0)
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    const setSeg = encodeURIComponent(String(specSetId).trim());
    const q: PaginationParams = {
      page: params.page,
      page_size: params.page_size,
    };
    if (this.scopeCatalogLikeCustomerPortal()) {
      q['view_publish'] = true;
    } else if (params.view_publish === true) {
      q['view_publish'] = true;
    }
    if (params.item_specs?.trim()) {
      q['item_specs'] = params.item_specs.trim();
    }
    if (params.price_min !== undefined && params.price_min !== null) {
      q['price_min'] = params.price_min;
    }
    if (params.price_max !== undefined && params.price_max !== null) {
      q['price_max'] = params.price_max;
    }
    return this.paginationService.getPaginatedData<any>(
      `${this.host}/CF/category/${path}/spec-set/${setSeg}/products/`,
      q
    );
  }

  /**
   * Customer portal category sidebar — same contract as admin `category-tree-levels` APIs.
   */
  public getCategoryTreeLevelsRoot(options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    viewPublish?: boolean;
    businessPartner?: string | null;
  }): Observable<any> {
    const params = this.buildCategoryTreeLevelsParams(options);
    return this.http.get(`${this.host}/CF/category-tree-levels/`, { params });
  }

  public getCategoryTreeLevelsChildren(
    categoryCode: string,
    options?: {
      page?: number;
      pageSize?: number;
      viewPublish?: boolean;
      businessPartner?: string | null;
    }
  ): Observable<any> {
    const path = categoryCode
      .split('/')
      .filter((s) => s.length > 0)
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    const params = this.buildCategoryTreeLevelsParams(options);
    return this.http.get(`${this.host}/CF/category-tree-levels/${path}/`, {
      params,
    });
  }

  /**
   * DEPRECATED - Use getCatalogMetadata() or getCategoryMetadata() instead
   * Kept for backward compatibility during migration
   */
  public getCategories(codeCategory, params: PaginationParams = {}): Observable<any> {
    // If no category code, get catalog list
    if (!codeCategory) {
      return this.getCatalogMetadata();
    }
    // Otherwise get single category metadata
    return this.getCategoryMetadata(codeCategory, false);
  }

  /**
   * DEPRECATED - Admin endpoints - kept for backward compatibility
   */
  public getCategoriesAdmin(categoryCode): Observable<any> {
    let query1 = new HttpParams().set('categoryCode', categoryCode);
    if (this.scopeCatalogLikeCustomerPortal()) {
      query1 = query1.set('view_publish', 'true');
    }
    return this.http.get(`${this.host}/CF/Admin-tree-structure/`, { params: query1 })
  }

  public getCategoriesForDetails(categoryCode): Observable<any> {
    return this.getCategoryMetadata(categoryCode, false);
  }

  public getCategoriesForDetailsAdmin(categoryCode): Observable<any> {
    let query1 = new HttpParams().set('categoryCode', categoryCode);
    if (this.scopeCatalogLikeCustomerPortal()) {
      query1 = query1.set('view_publish', 'true');
    }
    return this.http.get(`${this.host}/CF/Admin-tree-structure/`, { params: query1 })
  }

  public getCategoriesList(): Observable<any> {
    return this.http.get(`${this.host}/CF/api/categories/`, { withCredentials: true });
  }

  // Function to consume the retrieve language api
  public searchLanguage(query): Observable<any> {
    // query parameter
    const query1 = new HttpParams().set('languageCode', query);
    return this.http.get(`${this.host}/CF/cf_languages/`, { params: query1 })
  }

  // Function to consume the search category api. Pass catalogue only when filtering (true/false); omit to return all categories.
  public getCfCategory(query: string, catalogue?: boolean, params: PaginationParams = {}): Observable<any> {
    const searchParams: PaginationParams = {
      ...params,
      categoryCode: query
    };
    if (catalogue !== undefined) {
      searchParams['catalogue'] = catalogue;
    }
    if (this.scopeCatalogLikeCustomerPortal()) {
      searchParams['view_publish'] = true; // literal true, forced (ignores any false in `params`)
    }
    return this.http.get(`${this.host}/CF/category/`, { params: searchParams });
  }

  public createCfCategory(categoryData: any): Observable<any | HttpErrorResponse> {
    console.log("categoryData", categoryData)
    return this.http.post<any>(`${this.host}/CF/category/`, categoryData)
  }

  getCategory(id: string): Observable<any> {
    return this.http.get<any>(`${this.host}/${id}`);
  }

  updateCategory(category: any): Observable<any> {
    return this.http.put<any>(`${this.host}/${category.id}`, category);
  }

  // Function to consume the retrieve customer api by customer (details of each customer)
  public getCfCategoryById(categoryCode: string): Observable<any> {
    let params = new HttpParams();
    if (this.scopeCatalogLikeCustomerPortal()) {
      params = params.set('view_publish', 'true');
    }
    const opts = params.keys().length > 0 ? { params } : {};
    return this.http.get<any>(`${this.host}/CF/category/${categoryCode}`, opts);
  }

  public getCfCategorySearch(categoryCode: string, params: PaginationParams = {}): Observable<any> {
    const q: PaginationParams = {
      ...params,
      categoryCode,
    };
    if (this.scopeCatalogLikeCustomerPortal()) {
      q['view_publish'] = true; // literal true, forced (ignores any false in `params`)
    }
    return this.http.get(`${this.host}/CF/category/`, { params: q });
  }

  // Function to consume the update category api
  public updateCfCategory(category: any, categoryData: FormData): Observable<any | HttpErrorResponse> {
    return this.http.put<any>(`${this.host}/CF/category/${category}`, categoryData);
  }

  addSubcategory(parentId: string, subcategory: any): Observable<any> {
    return this.http.post<any>(`${this.host}/${parentId}/subcategories`, subcategory);
  }

  addProductToCategory(categoryId: string, product: any): Observable<any> {
    return this.http.post<any>(`${this.host}/${categoryId}/products`, product);
  }

  // ========== NAVIGATION TRACKING METHODS ==========

  /**
   * Set the current active category in the local state
   * @param activeCategory Active category object
   */
  // public setActiveCategory(activeCategory: any): void {
  //   this.activeCategorySubject.next(activeCategory);
  // }
  setActiveCategory(category: any): void {
    this.activeCategorySubject.next(category);
    localStorage.setItem('activeCategory', JSON.stringify(category));
  }

  /**
   * Get the current active category from the local state
   * @returns The current active category value
   */
  public getActiveCategory(): any {
    return this.activeCategorySubject.getValue();
  }

  /**
   * Add a category to the navigation history path
   * @param categoryCode The category code to add to the path
   */
  addToNavigationHistory(categoryCode: string): void {
    const currentHistory = this.navigationHistorySubject.value;
    // Check if the category is already at the end of the history
    if (currentHistory.length > 0 && currentHistory[currentHistory.length - 1] === categoryCode) {
      return; // Already the last item, no need to add
    }

    // Check if category already exists in the history
    const existingIndex = currentHistory.indexOf(categoryCode);
    if (existingIndex >= 0) {
      // Category exists in history - truncate the history up to this category
      const newHistory = currentHistory.slice(0, existingIndex + 1);
      this.navigationHistorySubject.next(newHistory);
      // Save to localStorage
      localStorage.setItem('categoryNavigationHistory', JSON.stringify(newHistory));
    } else {
      // Category doesn't exist - add it to the end
      const updatedHistory = [...currentHistory, categoryCode];
      this.navigationHistorySubject.next(updatedHistory);
      // Save to localStorage
      localStorage.setItem('categoryNavigationHistory', JSON.stringify(updatedHistory));
    }
  }

  public getNavigationHistory(): string[] {
    return this.navigationHistorySubject.getValue();
  }

  /**
   * Navigate to a specific position in the history
   * @param index The index to truncate the history to (inclusive)
   */
  public navigateToHistoryPosition(index: number): void {
    const currentHistory = this.navigationHistorySubject.getValue();

    if (index >= 0 && index < currentHistory.length) {
      const updatedHistory = currentHistory.slice(0, index + 1);
      this.navigationHistorySubject.next(updatedHistory);

      // Save to localStorage
      localStorage.setItem('categoryNavigationHistory', JSON.stringify(updatedHistory));
    }
  }

  /**
   * Get the current navigation history array
   * @returns The current navigation history value
   */


  /**
   * Reset the navigation history completely
   */
  public resetNavigationHistory(): void {
    this.navigationHistorySubject.next([]);
    localStorage.removeItem('categoryNavigationHistory');
  }

  /**
   * Go back one step in the navigation history
   */
  public goBackInHistory(): void {
    const currentHistory = this.navigationHistorySubject.getValue();
    if (currentHistory.length > 1) {
      const updatedHistory = currentHistory.slice(0, -1);
      this.navigationHistorySubject.next(updatedHistory);
      localStorage.setItem('categoryNavigationHistory', JSON.stringify(updatedHistory));
    }
  }

  /**
   * Get the last visited category code from history
   * @returns The last category code or null if history is empty
   */
  public getLastVisitedCategory(): string | null {
    const currentHistory = this.navigationHistorySubject.getValue();
    return currentHistory.length > 0 ? currentHistory[currentHistory.length - 1] : null;
  }

}
